import { useEffect, useState, type JSX } from 'react'
import Popover from '@mui/material/Popover'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { MONO_FONT } from '@/shared/typography'
import { clampNumber, formatNumberDisplay } from './formatNumber'

type Op = '+' | '\u2212' | '\u00d7' | '\u00f7'

interface NumberCalculatorProps {
  open: boolean
  anchorEl: HTMLElement | null
  onClose: () => void
  onApply: (value: number) => void
  initialValue: number | null
  precision: number
  scale: number
}

const DIGIT_ROWS: string[][] = [
  ['7', '8', '9', '\u00f7'],
  ['4', '5', '6', '\u00d7'],
  ['1', '2', '3', '\u2212'],
  ['0', '.', '%', '+'],
  ['C', '\u232b', '\u00b1', '='],
]

function computeOp(a: number, op: Op, b: number): { value: number; error: string | null } {
  switch (op) {
    case '+':
      return { value: a + b, error: null }
    case '\u2212':
      return { value: a - b, error: null }
    case '\u00d7':
      return { value: a * b, error: null }
    case '\u00f7':
      if (b === 0) return { value: 0, error: 'Cannot divide by zero' }
      return { value: a / b, error: null }
  }
}

export function NumberCalculator(props: NumberCalculatorProps): JSX.Element {
  const { open, anchorEl, onClose, onApply, initialValue, precision, scale } = props
  const theme = useTheme()
  const [display, setDisplay] = useState<string>('0')
  const [accumulator, setAccumulator] = useState<number | null>(null)
  const [pendingOp, setPendingOp] = useState<Op | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [freshEntry, setFreshEntry] = useState<boolean>(true)

  useEffect(() => {
    if (open) {
      const seeded = initialValue == null ? '0' : formatNumberDisplay(initialValue, precision, scale)
      setDisplay(seeded === '' ? '0' : seeded)
      setAccumulator(null)
      setPendingOp(null)
      setError(null)
      setFreshEntry(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function currentValue(): number {
    const n = Number(display)
    return Number.isFinite(n) ? n : 0
  }

  function handleDigit(digit: string): void {
    setError(null)
    if (freshEntry) {
      setDisplay(digit === '.' ? '0.' : digit)
      setFreshEntry(false)
      return
    }
    if (digit === '.' && display.includes('.')) return
    setDisplay((prev) => (prev === '0' && digit !== '.' ? digit : prev + digit))
  }

  function handleClear(): void {
    setDisplay('0')
    setAccumulator(null)
    setPendingOp(null)
    setError(null)
    setFreshEntry(true)
  }

  function handleBackspace(): void {
    setError(null)
    setDisplay((prev) => {
      if (prev.length <= 1 || (prev.length === 2 && prev.startsWith('-'))) return '0'
      return prev.slice(0, -1)
    })
  }

  function handleSign(): void {
    setError(null)
    setDisplay((prev) => {
      if (prev === '0') return prev
      return prev.startsWith('-') ? prev.slice(1) : `-${prev}`
    })
  }

  function handlePercent(): void {
    setError(null)
    setDisplay((prev) => {
      const n = Number(prev)
      if (!Number.isFinite(n)) return prev
      return String(n / 100)
    })
  }

  function handleOp(op: Op): void {
    if (error) return
    const value = currentValue()
    if (accumulator == null || pendingOp == null) {
      setAccumulator(value)
    } else {
      const { value: result, error: opError } = computeOp(accumulator, pendingOp, value)
      if (opError) {
        setError(opError)
        return
      }
      setAccumulator(result)
      setDisplay(String(result))
    }
    setPendingOp(op)
    setFreshEntry(true)
  }

  function handleEquals(): void {
    if (error) return
    const value = currentValue()
    if (accumulator == null || pendingOp == null) {
      applyResult(value)
      return
    }
    const { value: result, error: opError } = computeOp(accumulator, pendingOp, value)
    if (opError) {
      setError(opError)
      return
    }
    setDisplay(String(result))
    setAccumulator(null)
    setPendingOp(null)
    setFreshEntry(true)
    applyResult(result)
  }

  function applyResult(value: number): void {
    onApply(clampNumber(value, precision, scale))
    onClose()
  }

  function handleButton(label: string): void {
    if (label === 'C') {
      handleClear()
      return
    }
    if (label === '\u232b') {
      handleBackspace()
      return
    }
    if (label === '\u00b1') {
      handleSign()
      return
    }
    if (label === '%') {
      handlePercent()
      return
    }
    if (label === '=') {
      handleEquals()
      return
    }
    if (label === '+' || label === '\u2212' || label === '\u00d7' || label === '\u00f7') {
      handleOp(label)
      return
    }
    handleDigit(label)
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Escape') {
      onClose()
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      handleEquals()
    }
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-label': 'Number calculator',
          onKeyDown: handleKeyDown,
          sx: {
            p: 1.5,
            width: 260,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
          },
        },
      }}
    >
      <Box
        sx={{
          mb: 1,
          p: 1,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.text.primary, 0.04),
          textAlign: 'right',
        }}
      >
        <Typography
          sx={{ fontFamily: MONO_FONT, fontSize: 20, color: 'text.primary', wordBreak: 'break-all' }}
        >
          {display}
        </Typography>
        {error ? (
          <Typography sx={{ fontFamily: MONO_FONT, fontSize: 12, color: 'error.main', mt: 0.5 }}>
            {error}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75 }}>
        {DIGIT_ROWS.flat().map((label) => {
          const isOp = ['+', '\u2212', '\u00d7', '\u00f7', '='].includes(label)
          const isDestructive = label === 'C' || label === '\u232b'
          return (
            <Button
              key={label}
              type="button"
              aria-label={label}
              onClick={() => handleButton(label)}
              sx={{
                minWidth: 44,
                minHeight: 44,
                fontFamily: MONO_FONT,
                fontWeight: isOp ? 600 : 400,
                color: isOp
                  ? theme.palette.primary.main
                  : isDestructive
                    ? theme.palette.text.secondary
                    : theme.palette.text.primary,
                bgcolor: isOp ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  bgcolor: isOp
                    ? alpha(theme.palette.primary.main, 0.16)
                    : alpha(theme.palette.text.primary, 0.06),
                },
              }}
            >
              {label}
            </Button>
          )
        })}
      </Box>
    </Popover>
  )
}
